import React, { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import AdminPagination from "../../../CommonComponents/Pagination/AdminPagination";
import { networkService } from "../../../config/axios";
import "../admin.css";
import "./adminImpersonationLog.css";

const PER_PAGE = 50;

const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  active: "Active",
  declined: "Declined",
  expired: "Expired",
  cancelled: "Cancelled",
  enter_expired: "Not entered",
  ended: "Ended",
};

const END_REASON_LABELS = {
  admin_exit: "Admin exited",
  user_revoked: "User ended access",
  tab_closed: "Tab closed",
  admin_signed_out: "Admin signed out",
  user_signed_out: "User signed out",
  target_banned: "User suspended",
};

const EMPTY_FILTERS = { admin: "", user: "", status: "", from: "", to: "" };

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const PersonCell = ({ person, company }) =>
  person ? (
    <div className="impersonation-log-person">
      <span>{person.name}</span>
      {person.name !== person.email && <span className="impersonation-log-muted">{person.email}</span>}
      {company && <span className="impersonation-log-muted">{company}</span>}
    </div>
  ) : (
    <span className="impersonation-log-muted">Deleted user</span>
  );

const AdminImpersonationLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const params = new URLSearchParams({ page, per_page: PER_PAGE });
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      try {
        const response = await networkService(`admin/impersonation_logs/list.json?${params.toString()}`, "GET");
        if (cancelled) return;
        setLogs(response.data?.logs || []);
        setTotal(response.data?.total || 0);
      } catch (error) {
        if (!cancelled) setLogs([]);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, appliedFilters]);

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const hasFilters = Object.values(appliedFilters).some(Boolean);

  const columns = useMemo(() => [
    { Header: "Requested", accessor: "requested_at", Cell: ({ value }) => formatDate(value) },
    { Header: "Admin", accessor: "requester", Cell: ({ row }) => <PersonCell person={row.original.requester} company={row.original.requester_company} /> },
    { Header: "User", accessor: "target", Cell: ({ row }) => <PersonCell person={row.original.target} company={row.original.target_company} /> },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row, value }) => (
        <div className="impersonation-log-person">
          <span className={`impersonation-log-status impersonation-log-status--${value}`}>{STATUS_LABELS[value] || value}</span>
          {row.original.consent_bypassed && <span className="impersonation-log-muted">Consent bypassed</span>}
        </div>
      ),
    },
    {
      Header: "Reason",
      accessor: "reason",
      Cell: ({ value }) => (value ? <span className="impersonation-log-reason" title={value}>{value}</span> : "—"),
    },
    { Header: "Started", accessor: "started_at", Cell: ({ value }) => formatDate(value) },
    { Header: "Ended", accessor: "ended_at", Cell: ({ value }) => formatDate(value) },
    { Header: "Duration", accessor: "duration_seconds", Cell: ({ value }) => formatDuration(value) },
    { Header: "End reason", accessor: "end_reason", Cell: ({ value }) => END_REASON_LABELS[value] || "—" },
    {
      Header: "Admin IP / Location",
      accessor: "admin_ip",
      Cell: ({ row }) => {
        const log = row.original;
        const location = [log.admin_city, log.admin_region, log.admin_country].filter(Boolean).join(", ");
        return (
          <div className="impersonation-log-person" title={log.admin_user_agent || ""}>
            <span>{log.admin_ip || "—"}</span>
            {location && <span className="impersonation-log-muted">{location}</span>}
          </div>
        );
      },
    },
  ], []);

  return (
    <div className="admin-module">
      <div className="admin-content impersonation-log-page">
        <Breadcrumb string="Admin / Impersonation Log" />

        <div className="container impersonation-log-container">
          <div className="impersonation-log-header">
            <h4 className="impersonation-log-title">Impersonation Log</h4>
            <span className="impersonation-log-count">{total} {total === 1 ? "session" : "sessions"}</span>
          </div>

          <form className="impersonation-log-filters" onSubmit={applyFilters}>
            <input
              className="impersonation-log-input"
              placeholder="Admin name or email"
              value={filters.admin}
              onChange={(e) => updateFilter("admin", e.target.value)}
            />
            <input
              className="impersonation-log-input"
              placeholder="User name or email"
              value={filters.user}
              onChange={(e) => updateFilter("user", e.target.value)}
            />
            <select className="impersonation-log-input" value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <label className="impersonation-log-date">
              <span>From</span>
              <input className="impersonation-log-input" type="date" value={filters.from} onChange={(e) => updateFilter("from", e.target.value)} />
            </label>
            <label className="impersonation-log-date">
              <span>To</span>
              <input className="impersonation-log-input" type="date" value={filters.to} onChange={(e) => updateFilter("to", e.target.value)} />
            </label>
            <div className="impersonation-log-filter-actions">
              <button type="submit" className="impersonation-log-btn impersonation-log-btn--primary">Apply</button>
              {hasFilters && (
                <button type="button" className="impersonation-log-btn impersonation-log-btn--secondary" onClick={clearFilters}>Clear</button>
              )}
            </div>
          </form>

          <div className="top-parent admin-user-table-wrapper impersonation-log-table-wrapper">
            {loading ? (
              <p className="impersonation-log-state">Loading…</p>
            ) : logs.length ? (
              <CustomTable data={logs} columnData={columns} />
            ) : (
              <p className="impersonation-log-state">
                {hasFilters ? "No impersonation sessions match these filters." : "No impersonation sessions yet."}
              </p>
            )}
          </div>

          {total > PER_PAGE && (
            <AdminPagination dataPerPage={PER_PAGE} totalData={total} currentPage={page} onPageChange={setPage} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminImpersonationLogPage;
